import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type { PersistedUserFlow, Test } from "../types";
import { useApiMutation, useApiQuery } from "./index";
import type { WithDynamicUrl } from "./use-api-mutation";

type TagRequest = WithDynamicUrl<{ tags: string[] }>;

interface TagResponse {
  tags: string[];
}

interface TagSearchRequest {
  tags: string[];
  include_all: boolean;
}

export const useAvailableTags = () => {
  return useApiQuery<TagResponse>("/tags");
};

export const useAddTagsToTest = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useApiMutation<void, TagRequest>("POST", "/tests/{test_id}/tags", {
    onSuccess: () => {
      queryClient.invalidateQueries();
      enqueueSnackbar("Tags added successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to add tags: ${error.message}`, {
        variant: "error",
      });
    },
  });
};

export const useRemoveTagsFromTest = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useApiMutation<void, TagRequest>("DELETE", "/tests/{test_id}/tags", {
    onSuccess: () => {
      queryClient.invalidateQueries();
      enqueueSnackbar("Tags removed successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to remove tags: ${error.message}`, {
        variant: "error",
      });
    },
  });
};

export const useAddTagsToFlow = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useApiMutation<void, TagRequest>("POST", "/flows/{flow_id}/tags", {
    onSuccess: () => {
      queryClient.invalidateQueries();
      enqueueSnackbar("Tags added successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to add tags: ${error.message}`, {
        variant: "error",
      });
    },
  });
};

export const useRemoveTagsFromFlow = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useApiMutation<void, TagRequest>("DELETE", "/flows/{flow_id}/tags", {
    onSuccess: () => {
      queryClient.invalidateQueries();
      enqueueSnackbar("Tags removed successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to remove tags: ${error.message}`, {
        variant: "error",
      });
    },
  });
};

export const useSearchTestsByTags = () => {
  return useApiMutation<{ tests: Test[] }, TagSearchRequest>("POST", "/tests/search-by-tags");
};

export const useSearchFlowsByTags = () => {
  return useApiMutation<{ flows: PersistedUserFlow[] }, TagSearchRequest>(
    "POST",
    "/flows/search-by-tags",
  );
};
